import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Sparkles, ArrowRight, Check, Star } from "lucide-react";

export default function ReferralPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const referralSource = searchParams.get("ref");
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    // Se veio de um projeto cliente, mostra confetti
    if (referralSource?.includes("client")) {
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 3000);
    }
  }, [referralSource]);

  const benefits = [
    "Desenvolvimento profissional e personalizado",
    "Código limpo e documentado",
    "Suporte pós-entrega",
    "Tecnologias modernas e atuais",
    "Prazos cumpridos com rigor",
    "Preços justos e transparentes",
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 text-white overflow-hidden relative">
      {/* Confetti effect */}
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none z-50">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-confetti"
              style={{
                left: `${Math.random() * 100}%`,
                top: "-10px",
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: `${3 + Math.random() * 2}s`,
              }}
            >
              ✨
            </div>
          ))}
        </div>
      )}

      {/* Background effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 -left-20 w-96 h-96 bg-purple-600 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute top-40 -right-20 w-96 h-96 bg-blue-600 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
      </div>

      <div className="relative z-10 min-h-screen flex items-center justify-center px-6 py-20">
        <div className="max-w-4xl mx-auto text-center space-y-12">
          {/* --- LOGO FLUTUANTE (ANIMAÇÃO IGUAL A DO LEÃO) --- */}
          <div className="animate-bounce-slow">
            <div className="inline-flex p-8 rounded-3xl bg-gradient-to-br from-purple-500/20 to-blue-500/20 backdrop-blur-sm border border-white/20 hover:border-purple-500/50 transition-all">
              <img
                src="/assets/ralui-tech-logo.png"
                alt="Ralui Tech Logo"
                // Mudei de w-32 h-32 para w-64 h-64
                className="w-64 h-64 object-contain drop-shadow-2xl"
              />
            </div>
          </div>

          {/* Main Message */}
          <div className="space-y-6 animate-fade-in">
            {/* Badge de Visitante Especial */}
            {referralSource?.includes("client") && (
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/20 border border-purple-500/30 text-purple-300 text-sm mb-4">
                <Sparkles size={16} />
                <span>Visitante Especial Detectado!</span>
              </div>
            )}

            {referralSource?.includes("client") ? (
              /* --- TEXTO PERSUASIVO (Para quem veio de indicação) --- */
              <>
                <h1 className="text-5xl md:text-7xl font-bold leading-tight">
                  <span className="bg-gradient-to-r from-purple-400 via-blue-400 to-green-400 bg-clip-text text-transparent">
                    Gostou do que viu? 👀
                  </span>
                </h1>

                <p className="text-xl md:text-2xl text-slate-300 leading-relaxed max-w-3xl mx-auto">
                  Se você chegou até aqui, é porque a qualidade de{" "}
                  <span className="font-bold text-white">
                    um dos sites que desenvolvemos
                  </span>{" "}
                  chamou sua atenção.
                  <br />
                  <br />
                  Aquele selo da{" "}
                  <span className="font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                    RALUI TECH
                  </span>{" "}
                  no rodapé é a nossa assinatura de excelência. Que tal levarmos
                  esse mesmo padrão para o seu negócio?
                </p>
              </>
            ) : (
              /* --- TEXTO PADRÃO (Acesso direto) --- */
              <>
                <h1 className="text-5xl md:text-7xl font-bold leading-tight">
                  <span className="bg-gradient-to-r from-purple-400 via-blue-400 to-green-400 bg-clip-text text-transparent">
                    Olá, Visitante! 👋
                  </span>
                </h1>
                <p className="text-xl md:text-2xl text-slate-300 leading-relaxed max-w-3xl mx-auto">
                  Bem-vindo à{" "}
                  <span className="font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                    RALUI TECH
                  </span>
                  !
                  <br />
                  Transformamos ideias em soluções digitais incríveis! 🚀
                </p>
              </>
            )}
          </div>

          {/* Benefits Grid */}
          <div className="grid md:grid-cols-2 gap-4 max-w-3xl mx-auto pt-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="flex items-center gap-3 p-4 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 hover:border-purple-500/50 transition-all hover:scale-105 animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
                  <Check size={18} />
                </div>
                <span className="text-left text-slate-300">{benefit}</span>
              </div>
            ))}
          </div>

          {/* Social Proof */}
          {referralSource?.includes("client") && (
            <div className="p-8 rounded-2xl bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/20 max-w-2xl mx-auto">
              <div className="flex items-center justify-center gap-2 mb-4">
                <Star className="text-yellow-400 fill-yellow-400" size={20} />
                <Star className="text-yellow-400 fill-yellow-400" size={20} />
                <Star className="text-yellow-400 fill-yellow-400" size={20} />
                <Star className="text-yellow-400 fill-yellow-400" size={20} />
                <Star className="text-yellow-400 fill-yellow-400" size={20} />
              </div>
              <p className="text-lg text-slate-300 italic">
                "Nossa melhor propaganda é o sucesso dos nossos clientes.
                Ficamos felizes que você tenha notado!"
              </p>
            </div>
          )}

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-8">
            <a
              href="#contact"
              onClick={(e) => {
                e.preventDefault();
                navigate("/?scroll=contact");
              }}
              className="group px-8 py-4 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full font-semibold text-lg hover:shadow-2xl hover:shadow-purple-500/50 transition-all flex items-center gap-2"
            >
              Quero um Projeto Assim!
              <ArrowRight
                className="group-hover:translate-x-1 transition-transform"
                size={20}
              />
            </a>
            <button
              onClick={() => navigate("/")}
              className="px-8 py-4 border-2 border-purple-500/50 rounded-full font-semibold text-lg hover:bg-purple-500/10 transition-all"
            >
              Conhecer a Ralui Tech
            </button>
          </div>

          {/* --- ESTATÍSTICAS (Trazendo de volta!) --- */}
          <div className="grid grid-cols-3 gap-8 pt-16 max-w-2xl mx-auto animate-fade-in-up animation-delay-200">
            <div className="space-y-2">
              <div className="text-4xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                100%
              </div>
              <div className="text-sm text-slate-400">Satisfação</div>
            </div>
            <div className="space-y-2">
              <div className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-green-400 bg-clip-text text-transparent">
                24/7
              </div>
              <div className="text-sm text-slate-400">Suporte</div>
            </div>
            <div className="space-y-2">
              <div className="text-4xl font-bold bg-gradient-to-r from-green-400 to-purple-400 bg-clip-text text-transparent">
                Rápido
              </div>
              <div className="text-sm text-slate-400">Entrega</div>
            </div>
          </div>
        </div>
      </div>

      {/* ESTILOS (Trazendo de volta para garantir a animação) */}
      <style>{`
        @keyframes blob {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        
        /* A ANIMAÇÃO QUE VOCÊ GOSTOU */
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-20px); }
        }
        .animate-bounce-slow {
          animation: bounce-slow 3s ease-in-out infinite;
        }

        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-fade-in {
          animation: fade-in 1s ease-out;
        }
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.6s ease-out forwards;
          opacity: 0;
        }
        @keyframes confetti {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
        .animate-confetti {
          animation: confetti 3s ease-out forwards;
        }
      `}</style>
    </div>
  );
}
